<script lang="ts" setup>
  import { computed } from 'vue';
  import { useShared, useTranslator } from '@othercommerce/foundation';
  import { Unit, useUnitFormatter } from '@othercommerce/domain';
  import { Features } from '@shared/features';

  interface HasPrice {
    stockUnit: Unit,
    stockUnitBase: string,
    priceNet: string | null,
    priceGross: string | null,
    regularNet: string | null,
    regularGross: string | null,
    isMedicinalProduct: boolean,
    promotion: boolean,
  }

  interface Props {
    price: HasPrice,
    presentation?: boolean,
    current?: boolean,
    row?: boolean,
  }

  const props = withDefaults(defineProps<Props>(), {
    presentation: false,
    current: false,
    row: false,
  });

  const shared = useShared();
  const translator = useTranslator();
  const unitFormatter = useUnitFormatter();

  const priceClasses = computed(() => {
    return {
      'price--presentation': props.presentation,
      'price--row': props.row,
    };
  });

  const regular = computed(() => {
    if (shared.value.includeVat) {
      return props.price.regularGross;
    } else {
      return props.price.regularNet;
    }
  });

  const price = computed(() => {
    if (shared.value.includeVat) {
      return props.price.priceGross;
    } else {
      return props.price.priceNet;
    }
  });

  const promotion = computed(() => {
    if (props.price.promotion && price.value !== null && regular.value !== null) {
      return !props.current && price.value != regular.value;
    }

    return false;
  });

  const unit = computed(() => {
    let stockUnit = props.price.stockUnit;
    let stockBase = props.price.stockUnitBase;

    if (stockBase === '1.0000') {
      return unitFormatter.symbol(stockUnit);
    }

    return unitFormatter.short(stockUnit, stockBase);
  });

  const message = computed(() => {
    if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled) {
      return shared.value.includeVat
        ? translator.trans('SHOP.COMMON.LABEL.BRUTTO')
        : translator.trans('SHOP.COMMON.LABEL.NETTO');
    }

    return shared.value.includeVat
      ? translator.trans('SHOP.COMMON.LABEL.BRUTTO_PER_UNIT', { unit: unit.value })
      : translator.trans('SHOP.COMMON.LABEL.NETTO_PER_UNIT', { unit: unit.value });
  });

  const priceStatus = computed(() => {
    return !(Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled && props.price.isMedicinalProduct);
  });
</script>
<template>
  <div :class="priceClasses" class="price">
    <template v-if="priceStatus">
      <SfMoney v-if="promotion && regular !== null" :value="regular" class="price__catalog" />
      <div v-if="price !== undefined && price !== null" class="price__current">
        <SfMoney :value="price" />
        <span class="price__per" v-html="message" />
      </div>
    </template>
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-product-price-color: var(--color-default);
    --sf-product-price-font-family: var(--font-family-sans);

    --sf-product-price-current-font-size: 1rem;
    --sf-product-price-current-font-line-height: 1.25rem;
    --sf-product-price-current-font-weight: var(--font-weight-bold);
    --sf-product-price-current-color: var(--color-default);

    --sf-product-price-per-font-size: 0.75rem;
    --sf-product-price-per-font-line-height: 0.75rem;
    --sf-product-price-per-font-weight: var(--font-weight-regular);
    --sf-product-price-per-color: var(--color-default);

    --sf-product-price-catalog-font-size: 0.75rem;
    --sf-product-price-catalog-font-line-height: 0.75rem;
    --sf-product-price-catalog-font-weight: var(--font-weight-regular);
    --sf-product-price-catalog-color: var(--danger-color);

    // modifiers

    --sf-product-price-presentation-gap: 0.75rem;

    --sf-product-price-presentation-catalog-font-size: var(--font-size-sm);
    --sf-product-price-presentation-catalog-font-line-height: 1.25;

    --sf-product-price-presentation-current-font-size: 1.5rem;
    --sf-product-price-presentation-current-font-line-height: 1;

    --sf-product-price-gap-row: 0.25rem;
  }

  .price {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &--row {
      flex-direction: row;
      justify-content: flex-end;
      text-align: right;
      gap: var(--sf-product-price-gap-row);
    }

    &--presentation {
      .price__catalog {
        font-size: var(--sf-product-price-presentation-catalog-font-size);
        line-height: var(--sf-product-price-presentation-catalog-font-line-height);
      }

      .price__current {
        font-size: var(--sf-product-price-presentation-current-font-size);
        line-height: var(--sf-product-price-presentation-current-font-line-height);
      }
    }

    &__catalog {
      text-decoration: line-through;
      color: var(--sf-product-price-catalog-color);
      font-size: var(--sf-product-price-catalog-font-size);
      line-height: var(--sf-product-price-catalog-font-line-height);
      font-weight: var(--sf-product-price-catalog-font-weight);
    }

    &__current {
      color: var(--sf-product-price-current-color);
      font-size: var(--sf-product-price-current-font-size);
      line-height: var(--sf-product-price-current-font-line-height);
      font-weight: var(--sf-product-price-current-font-weight);
    }

    &__per {
      display: block;
      font-size: var(--sf-product-price-per-font-size);
      line-height: var(--sf-product-price-per-font-line-height);
      font-weight: var(--sf-product-price-per-font-weight);
      color: var(--sf-product-price-per-color);
    }
  }
</style>
